import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useRentals,
  findApprovedApplicant,
  isListingDeletable,
  PLATFORMS,
  RentalListing,
  RentalLead,
} from "@/hooks/use-rentals";
import { DeleteConfirmDialog } from "@/components/property-management/DeleteConfirmDialog";
import { CreateListingDialog } from "@/components/rentals/CreateListingDialog";
import { PublishDialog } from "@/components/rentals/PublishDialog";
import { AddLeadDialog } from "@/components/rentals/AddLeadDialog";
import { ListingPhotosDialog } from "@/components/rentals/ListingPhotosDialog";
import { ListingDetailSheet } from "@/components/rentals/ListingDetailSheet";
import { ApplicationDetailSheet } from "@/components/rentals/ApplicationDetailSheet";
import { appliedForLabel } from "@/lib/rental-applied-for";
import { cn } from "@/lib/utils";
import {
  Plus,
  Users,
  Megaphone,
  Pause,
  Play,
  TrendingUp,
  Building2,
  Pencil,
  Mail,
  CalendarDays,
  FileEdit,
  RotateCcw,
  X,
  Globe,
  Trash2,
  Archive,
} from "lucide-react";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import { usePortfolioEligibleForListing } from "@/hooks/use-portfolio-eligible-for-listing";
import { filterListingsForAddLead } from "@/hooks/use-listings-for-add-lead";

// ── Status dot colors ──────────────────────────────────────────────────────
const statusDot: Record<string, string> = {
  active: "bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.18)]",
  draft: "bg-muted-foreground/60",
  paused: "bg-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.18)]",
  rented: "bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.18)]",
  archived: "bg-muted-foreground/40",
};

const statusLabel: Record<string, string> = {
  active: "Live",
  draft: "Draft",
  paused: "Paused",
  rented: "Rented",
  archived: "Archived",
};

const leadStatusBadge: Record<string, string> = {
  new: "bg-blue-500/15 text-blue-400 border-blue-500/25",
  contacted: "bg-secondary/15 text-secondary border-secondary/25",
  showing_scheduled: "bg-amber-400/15 text-amber-400 border-amber-400/25",
  application_sent: "bg-primary/15 text-primary border-primary/25",
  approved: "bg-green-500/15 text-green-400 border-green-500/25",
  denied: "bg-destructive/15 text-destructive border-destructive/25",
  lost: "bg-muted text-muted-foreground border-border",
  archived: "bg-muted text-muted-foreground border-border",
};

const synStatusBadge: Record<string, string> = {
  pending: "bg-amber-400/15 text-amber-400 border-amber-400/25",
  published: "bg-green-500/15 text-green-400 border-green-500/25",
  failed: "bg-destructive/15 text-destructive border-destructive/25",
  removed: "bg-muted text-muted-foreground border-border",
};

// ── Platform pips ──────────────────────────────────────────────────────────
const platformPip: Record<string, { bg: string; initial: string }> = {
  zillow: { bg: "bg-[#006aff]", initial: "Z" },
  apartments_com: { bg: "bg-[#00838f]", initial: "A" },
  realtor_com: { bg: "bg-[#d92228]", initial: "R" },
  trulia: { bg: "bg-[#00b04f]", initial: "T" },
  rent_com: { bg: "bg-[#f5a623]", initial: "R" },
  facebook: { bg: "bg-[#1877f2]", initial: "F" },
  craigslist: { bg: "bg-[#5d2da9]", initial: "C" },
};

function PlatformPip({ platformId }: { platformId: string }) {
  const pip = platformPip[platformId];
  if (!pip) return null;
  return (
    <span
      className={cn(
        "inline-grid place-items-center w-5 h-5 rounded-[5px] text-[10px] font-black text-white flex-shrink-0",
        pip.bg,
      )}
    >
      {pip.initial}
    </span>
  );
}

// ── Relative date helper ───────────────────────────────────────────────────
function fmtRelDays(iso: string | null | undefined) {
  if (!iso) return "—";
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / 86400000),
  );
  return days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}

// ── Listing Card ───────────────────────────────────────────────────────────
interface ListingCardProps {
  listing: RentalListing;
  leadCount: number;
  publishedPlatforms: { id: string; platform: string }[];
  onCardClick: () => void;
  onViewApplications: () => void;
  onEdit: () => void;
  onPublish: () => void;
  onActivate: () => void;
  onPause: () => void;
  onRemoveFromRent: () => void;
  onDelete: () => void;
  propData: any;
}

function ListingCard({
  listing,
  leadCount,
  publishedPlatforms,
  onCardClick,
  onViewApplications,
  onEdit,
  onPublish,
  onActivate,
  onPause,
  onRemoveFromRent,
  onDelete,
  propData,
}: ListingCardProps) {
  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  const heroPhoto = listing.photos?.[0];
  const addr1 = propData?.address || propData?.street_address || listing.title;
  const addr2 = [propData?.city, propData?.state].filter(Boolean).join(", ");

  // Specs — prefer the listing's own values, fall back to portfolio property data
  const beds = listing.beds ?? propData?.bedrooms ?? propData?.beds ?? null;
  const baths = listing.baths ?? propData?.bathrooms ?? propData?.baths ?? null;
  const sqft = listing.sqft ?? propData?.square_feet ?? propData?.sqft ?? null;
  const lease =
    listing.lease_term_months === 0
      ? "M2M"
      : listing.lease_term_months
        ? `${listing.lease_term_months} mo`
        : "—";

  const datedLabel =
    listing.status === "draft"
      ? "Saved " + fmtRelDays(listing.updated_at)
      : "Listed " + fmtRelDays(listing.created_at);

  // A rented unit has a tenancy on it, so it gets "Remove from rent" instead of
  // delete. Applications don't block deletion — they're archived into Applicant
  // History rather than destroyed.
  const canDelete = isListingDeletable(listing);
  const hasStatusToggle =
    listing.status === "active" ||
    listing.status === "paused" ||
    listing.status === "draft";

  // Status toggle button — full-width, mirrors the "Remove from rent" button
  const StatusToggle = () => {
    if (listing.status === "active") {
      return (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs font-semibold justify-center gap-1.5"
          onClick={(e) => {
            e.stopPropagation();
            onPause();
          }}
        >
          <Pause className="h-3.5 w-3.5" /> Pause listing
        </Button>
      );
    }
    if (listing.status === "paused" || listing.status === "draft") {
      return (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs font-semibold justify-center gap-1.5"
          onClick={(e) => {
            e.stopPropagation();
            onActivate();
          }}
        >
          <Play className="h-3.5 w-3.5" /> Activate listing
        </Button>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col transition-colors hover:border-border/70 group">
      {/* Hero */}
      <div
        className="relative h-44 bg-muted flex-shrink-0 cursor-pointer"
        onClick={onCardClick}
      >
        {heroPhoto ? (
          <img src={heroPhoto} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="w-10 h-10 text-muted-foreground/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/55 pointer-events-none" />

        {/* Status pill */}
        <span className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-full px-2.5 py-[5px] text-[11.5px] font-semibold bg-background/70 backdrop-blur-sm border border-white/10 text-foreground">
          <span
            className={cn(
              "w-[7px] h-[7px] rounded-full flex-shrink-0",
              statusDot[listing.status] ?? statusDot.draft,
            )}
          />
          {statusLabel[listing.status] ?? listing.status}
        </span>

        {/* Rent tag */}
        <span className="absolute top-3 right-3 z-10 rounded-full px-3 py-[5px] text-[12.5px] font-bold bg-background/70 backdrop-blur-sm border border-white/10 text-foreground">
          {fmt(listing.rent_amount)}
          <span className="text-muted-foreground font-medium text-[11.5px]">
            {" "}
            /mo
          </span>
        </span>
      </div>

      {/* Body */}
      <div className="p-[18px] flex flex-col gap-3 flex-1">
        {/* Address */}
        <div className="cursor-pointer" onClick={onCardClick}>
          <div className="font-bold text-base leading-snug text-foreground">
            {addr1}
          </div>
          {addr2 && (
            <div className="text-[12.5px] text-muted-foreground mt-0.5">
              {addr2}
            </div>
          )}
        </div>

        {/* Specs row: BEDS | BATHS | SQFT | LEASE */}
        <div className="flex gap-[18px] py-1">
          {[
            { label: "BEDS", value: beds ?? "—" },
            { label: "BATHS", value: baths ?? "—" },
            { label: "SQFT", value: sqft ?? "—" },
            { label: "LEASE", value: lease },
          ].map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-0.5">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
              </div>
              <div className="text-[13.5px] font-semibold text-foreground">
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* Syndicated to */}
        {publishedPlatforms.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-muted-foreground/70">
              Syndicated to
            </span>
            {publishedPlatforms.slice(0, 5).map((s) => (
              <PlatformPip key={s.id} platformId={s.platform} />
            ))}
            {publishedPlatforms.length > 5 && (
              <span className="text-[11px] text-muted-foreground">
                +{publishedPlatforms.length - 5}
              </span>
            )}
          </div>
        )}

        {/* Meta row */}
        <div className="flex justify-between items-center pt-2 border-t border-border text-[12.5px] text-muted-foreground mt-auto">
          <span className="flex items-center gap-1.5">
            <Mail className="w-[13px] h-[13px] text-muted-foreground/60" />
            <strong className="text-foreground font-bold">
              {leadCount}
            </strong>{" "}
            inquiries
            {/* highlight new leads in blue if any — could be extended with newLeadCount per listing */}
          </span>
          <span className="flex items-center gap-1.5">
            <CalendarDays className="w-[13px] h-[13px] text-muted-foreground/60" />
            {datedLabel}
          </span>
        </div>

        {/* Actions: [View applications] [Edit] [Publish] */}
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs font-semibold justify-center gap-1.5"
            onClick={(e) => {
              e.stopPropagation();
              onViewApplications();
            }}
          >
            <Users className="h-3.5 w-3.5" /> Applications
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs font-semibold justify-center gap-1.5"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs font-semibold justify-center gap-1.5"
            onClick={(e) => {
              e.stopPropagation();
              onPublish();
            }}
          >
            <Globe className="h-3.5 w-3.5" /> Publish
          </Button>
        </div>

        {/* Activate / Pause + Delete — the status toggle and the destructive
            delete share a row. A rented listing gets neither: it shows
            "Remove from rent" below instead. */}
        {(hasStatusToggle || canDelete) && (
          <div className="flex gap-2">
            {hasStatusToggle && (
              <div className="flex-1 min-w-0">
                <StatusToggle />
              </div>
            )}
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                aria-label="Delete listing"
                title="Delete listing"
                className={cn(
                  "text-xs font-semibold justify-center gap-1.5",
                  "border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive",
                  hasStatusToggle ? "w-9 px-0 flex-shrink-0" : "w-full",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {!hasStatusToggle && "Delete listing"}
              </Button>
            )}
          </div>
        )}

        {/* Remove from rent — frees the unit + declines the approved applicant */}
        {listing.status === "rented" && (
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs font-semibold justify-center gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFromRent();
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Remove from rent
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────
function EmptyListings({ onNew }: { onNew: () => void }) {
  return (
    <div className="text-center py-14 px-6 border border-dashed border-border rounded-xl bg-card flex flex-col items-center gap-3.5 max-w-[620px] mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-destructive/10 text-destructive grid place-items-center">
        <Megaphone className="w-8 h-8" />
      </div>
      <div>
        <h3 className="text-[19px] font-bold text-foreground mb-1">
          No listings yet
        </h3>
        <p className="text-[13.5px] text-muted-foreground max-w-[460px] leading-relaxed">
          List your owned properties for rent, manage inquiries from prospective
          tenants, and track each unit through the leasing lifecycle.
        </p>
      </div>
      <Button onClick={onNew} className="mt-1">
        <Plus className="h-4 w-4 mr-1.5" /> New listing
      </Button>
      <div className="grid grid-cols-3 gap-3 max-w-[720px] w-full mt-2">
        {[
          {
            step: "Step 1",
            title: "Choose a property",
            desc: "Pick any owned property from your portfolio to create a listing.",
          },
          {
            step: "Step 2",
            title: "Set price & terms",
            desc: "Define rent, deposit, lease term, pet policy, and screening criteria.",
          },
          {
            step: "Step 3",
            title: "Publish to platforms",
            desc: "Syndicate to Zillow, Apartments.com, Realtor.com, and more in one click.",
          },
        ].map((s) => (
          <div
            key={s.step}
            className="bg-muted border border-border rounded-xl p-3.5 text-left"
          >
            <div className="text-[11px] font-bold text-destructive uppercase tracking-wide mb-1">
              {s.step}
            </div>
            <h4 className="text-[13.5px] font-semibold text-foreground mb-1">
              {s.title}
            </h4>
            <p className="text-[12px] text-muted-foreground leading-snug">
              {s.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function Rentals() {
  const {
    listings,
    leads,
    activeLeads,
    archivedLeads,
    syndications,
    isLoading,
    activeListings,
    totalLeads,
    newLeads,
    getSyndicationsForListing,
    getLeadsForListing,
    updateListing,
    deleteListing,
    updateLead,
  } = useRentals();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingListing, setEditingListing] = useState<RentalListing | null>(
    null,
  );
  const [publishListing, setPublishListing] = useState<RentalListing | null>(
    null,
  );
  const [leadOpen, setLeadOpen] = useState(false);
  const [photosListing, setPhotosListing] = useState<RentalListing | null>(
    null,
  );
  const [detailListing, setDetailListing] = useState<RentalListing | null>(
    null,
  );
  const [selectedLead, setSelectedLead] = useState<RentalLead | null>(null);
  const [deletingListing, setDeletingListing] = useState<RentalListing | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState("listings");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [appFilters, setAppFilters] = useState({
    source: "all",
    status: "all",
    listingId: "all",
  });

  const { savedProperties } = useSavedProperties();
  const { eligiblePortfolio, isLoading: eligiblePortfolioLoading } =
    usePortfolioEligibleForListing();
  // Only properties not already listed can start a new listing — the dialog
  // shows already-listed ones disabled, so the button gates on selectable ones.
  const selectableCount = eligiblePortfolio.filter(
    (p) => !p.alreadyListed,
  ).length;

  // Active listings only — the Add-lead button shows when at least one exists.
  const listingsForAddLead = useMemo(
    () => filterListingsForAddLead(listings),
    [listings],
  );

  // Resolved against every pipeline property, not `usePortfolio` — that hook
  // narrows to Closed-Won minus anything back in active rehab, so a listing (or
  // an archived applicant) whose property sits outside that slice lost its
  // address entirely. The row here only needs the property's cached data.
  const getPropertyData = (pipelinePropertyId: string) =>
    savedProperties.find((p) => p.id === pipelinePropertyId)?.property_data ??
    null;

  const getPlatformName = (id: string) =>
    PLATFORMS.find((p) => p.id === id)?.name || id;

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  // Split listings into active (non-draft) and drafts
  const draftListings = listings.filter((l) => l.status === "draft");
  const activeListing = listings.filter((l) => l.status !== "draft");
  const filteredListings =
    statusFilter === "all"
      ? activeListing
      : activeListing.filter((l) => l.status === statusFilter);

  // Filter leads for Applications tab. Archived applications are excluded — they
  // live in the Applicant History tab, and their listing no longer exists.
  const anyAppFilter =
    appFilters.source !== "all" ||
    appFilters.status !== "all" ||
    appFilters.listingId !== "all";
  const filteredLeads = activeLeads.filter(
    (l) =>
      (appFilters.source === "all" || l.platform === appFilters.source) &&
      (appFilters.status === "all" || l.status === appFilters.status) &&
      (appFilters.listingId === "all" || l.listing_id === appFilters.listingId),
  );
  // Unique platforms actually present in leads
  const leadPlatforms = [
    ...new Set(activeLeads.map((l) => l.platform).filter(Boolean)),
  ];
  // Listings that have at least one lead
  const listingsWithLeads = listings.filter((l) =>
    activeLeads.some((ld) => ld.listing_id === l.id),
  );

  // Delete doesn't delist from the platforms yet, so the confirmation warns when
  // the listing is still live somewhere.
  const publishedPlatformCount = deletingListing
    ? getSyndicationsForListing(deletingListing.id).filter(
        (s) => s.status === "published",
      ).length
    : 0;
  // Applications that will be archived into Applicant History by the delete.
  const deletingLeadCount = deletingListing
    ? getLeadsForListing(deletingListing.id).length
    : 0;

  const handleDeleteListing = () => {
    if (!deletingListing) return;
    const { id } = deletingListing;
    deleteListing.mutate(id, {
      onSuccess: () => {
        setDeletingListing(null);
        // The Applications filter may point at the listing we just removed —
        // drop back to "all" so the tab isn't filtering on a dead id.
        setAppFilters((f) =>
          f.listingId === id ? { ...f, listingId: "all" } : f,
        );
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="text-[30px] font-extrabold tracking-tight text-foreground">
            Rentals
          </h1>
          <p className="text-muted-foreground text-sm mt-1.5 max-w-xl">
            List your owned properties for rent, manage inquiries from
            prospective tenants, and track each unit through the leasing
            lifecycle.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          disabled={eligiblePortfolioLoading || selectableCount === 0}
          title={
            selectableCount === 0 && !eligiblePortfolioLoading
              ? "No properties available to list. A property must be in Closed-Won and out of active rehab (and not already listed) to create a listing."
              : undefined
          }
        >
          <Plus className="h-4 w-4 mr-1.5" /> New listing
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        {[
          {
            label: "Active listings",
            value: activeListings,
            icon: <Megaphone className="h-3.5 w-3.5 text-green-400" />,
          },
          {
            label: "Applications",
            value: totalLeads,
            icon: <Users className="h-3.5 w-3.5 text-blue-400" />,
          },
          {
            label: "New applications",
            value: newLeads,
            icon: <TrendingUp className="h-3.5 w-3.5 text-amber-400" />,
          },
          {
            label: "Drafts",
            value: draftListings.length,
            icon: <FileEdit className="h-3.5 w-3.5 text-muted-foreground" />,
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="bg-card border border-border rounded-[10px] px-[18px] py-4 flex flex-col gap-1.5"
          >
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {kpi.icon}
              {kpi.label}
            </div>
            <div className="text-[26px] font-extrabold text-foreground leading-none">
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Tab bar */}
        <div className="border-b border-border">
          <TabsList className="bg-transparent h-auto p-0 gap-1 rounded-none">
            {[
              {
                value: "listings",
                label: "Listings",
                count: activeListing.length,
              },
              { value: "drafts", label: "Drafts", count: draftListings.length },
              {
                value: "applications",
                label: "Applications",
                count: activeLeads.length,
              },
              {
                value: "history",
                label: "Applicant History",
                count: archivedLeads.length,
              },
            ].map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={cn(
                  "bg-transparent border-0 rounded-none px-3.5 py-2.5 text-[13.5px] font-semibold",
                  "text-muted-foreground border-b-2 border-transparent mb-[-1px]",
                  "data-[state=active]:text-destructive data-[state=active]:border-destructive data-[state=active]:bg-transparent",
                  "data-[state=active]:shadow-none",
                )}
              >
                {tab.label}
                <span
                  className={cn(
                    "ml-2 px-1.5 py-px rounded-full text-[11px] font-bold border",
                    "bg-muted border-border text-muted-foreground",
                    "[[data-state=active]_&]:bg-destructive/12 [[data-state=active]_&]:border-destructive/22 [[data-state=active]_&]:text-destructive",
                  )}
                >
                  {tab.count}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* ── Listings tab ── */}
        <TabsContent value="listings" className="mt-5">
          {/* Filter bar */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              {filteredListings.length} listing
              {filteredListings.length !== 1 ? "s" : ""}
              {statusFilter !== "all" ? ` · ${statusFilter}` : ""}
            </p>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="rented">Rented</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredListings.length === 0 ? (
            <EmptyListings onNew={() => setCreateOpen(true)} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredListings.map((listing) => {
                const syns = getSyndicationsForListing(listing.id);
                const lds = getLeadsForListing(listing.id);
                const publishedPlatforms = syns.filter(
                  (s) => s.status === "published",
                );
                const propData = getPropertyData(listing.pipeline_property_id);
                return (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    leadCount={lds.length}
                    publishedPlatforms={publishedPlatforms}
                    propData={propData}
                    onCardClick={() => setDetailListing(listing)}
                    onViewApplications={() => {
                      setAppFilters((f) => ({ ...f, listingId: listing.id }));
                      setActiveTab("applications");
                    }}
                    onEdit={() => {
                      setEditingListing(listing);
                    }}
                    onPublish={() => setPublishListing(listing)}
                    onActivate={() =>
                      updateListing.mutate({ id: listing.id, status: "active" })
                    }
                    onPause={() =>
                      updateListing.mutate({ id: listing.id, status: "paused" })
                    }
                    onDelete={() => setDeletingListing(listing)}
                    onRemoveFromRent={() => {
                      const approvedLeads = leads.filter(
                        (l) =>
                          l.listing_id === listing.id &&
                          l.status === "approved",
                      );
                      if (approvedLeads.length > 0) {
                        // Declining the approved applicant frees the unit —
                        // updateLead's side-effect flips the listing back to
                        // "active" automatically.
                        approvedLeads.forEach((l) =>
                          updateLead.mutate({ id: l.id, status: "denied" }),
                        );
                      } else {
                        updateListing.mutate({
                          id: listing.id,
                          status: "active",
                        });
                      }
                    }}
                  />
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Drafts tab ── */}
        <TabsContent value="drafts" className="mt-5">
          {draftListings.length === 0 ? (
            <div className="text-center py-14 px-6 border border-dashed border-border rounded-xl bg-card flex flex-col items-center gap-3.5 max-w-[620px] mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-muted border border-border grid place-items-center">
                <FileEdit className="w-7 h-7 text-muted-foreground/50" />
              </div>
              <div>
                <h3 className="text-[17px] font-bold text-foreground mb-1">
                  No drafts saved
                </h3>
                <p className="text-[13.5px] text-muted-foreground max-w-[420px] leading-relaxed">
                  Start a listing and click "Save as draft" to keep it here
                  until you're ready to publish.
                </p>
              </div>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" /> New listing
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {draftListings.map((listing) => {
                const syns = getSyndicationsForListing(listing.id);
                const lds = getLeadsForListing(listing.id);
                const publishedPlatforms = syns.filter(
                  (s) => s.status === "published",
                );
                const propData = getPropertyData(listing.pipeline_property_id);
                return (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    leadCount={lds.length}
                    publishedPlatforms={publishedPlatforms}
                    propData={propData}
                    onCardClick={() => setDetailListing(listing)}
                    onViewApplications={() => {
                      setAppFilters((f) => ({ ...f, listingId: listing.id }));
                      setActiveTab("applications");
                    }}
                    onEdit={() => setEditingListing(listing)}
                    onPublish={() => setPublishListing(listing)}
                    onActivate={() =>
                      updateListing.mutate({ id: listing.id, status: "active" })
                    }
                    onPause={() =>
                      updateListing.mutate({ id: listing.id, status: "paused" })
                    }
                    onDelete={() => setDeletingListing(listing)}
                    onRemoveFromRent={() => {}}
                  />
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Applications tab ── */}
        <TabsContent value="applications" className="mt-5 space-y-3">
          {/* Filter bar */}
          <div className="flex items-end gap-3 flex-wrap px-4 py-3 bg-card border border-border rounded-[10px]">
            <div className="flex flex-col gap-1 min-w-0">
              <label className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                Source
              </label>
              <Select
                value={appFilters.source}
                onValueChange={(v) =>
                  setAppFilters((f) => ({ ...f, source: v }))
                }
              >
                <SelectTrigger className="h-[34px] min-w-[150px] text-[13px] bg-muted border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {leadPlatforms.map((pid) => (
                    <SelectItem key={pid!} value={pid!}>
                      {getPlatformName(pid!)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 min-w-0">
              <label className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                Status
              </label>
              <Select
                value={appFilters.status}
                onValueChange={(v) =>
                  setAppFilters((f) => ({ ...f, status: v }))
                }
              >
                <SelectTrigger className="h-[34px] min-w-[150px] text-[13px] bg-muted border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="showing_scheduled">
                    Showing scheduled
                  </SelectItem>
                  <SelectItem value="application_sent">
                    Application sent
                  </SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="denied">Denied</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 min-w-0">
              <label className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                Listing
              </label>
              <Select
                value={appFilters.listingId}
                onValueChange={(v) =>
                  setAppFilters((f) => ({ ...f, listingId: v }))
                }
              >
                <SelectTrigger className="h-[34px] min-w-[180px] text-[13px] bg-muted border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All listings</SelectItem>
                  {listingsWithLeads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1" />
            <span className="text-[12px] text-muted-foreground h-[34px] flex items-center whitespace-nowrap">
              Showing{" "}
              <strong className="text-foreground mx-1">
                {filteredLeads.length}
              </strong>{" "}
              of {activeLeads.length}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-[34px] text-[12px] font-semibold gap-1.5"
              disabled={!anyAppFilter}
              onClick={() =>
                setAppFilters({
                  source: "all",
                  status: "all",
                  listingId: "all",
                })
              }
            >
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
            {listingsForAddLead.length > 0 && (
              <Button
                size="sm"
                className="h-[34px] text-[12px] gap-1.5"
                onClick={() => setLeadOpen(true)}
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            )}
          </div>

          {/* Applications table */}
          {activeLeads.length === 0 ? (
            <div className="text-center py-14 border border-dashed border-border rounded-xl bg-card text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No applications yet.</p>
              <p className="text-xs mt-1 max-w-xs mx-auto">
                When you publish a listing, applications from prospective
                tenants will show up here.
              </p>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-border rounded-xl bg-card text-muted-foreground">
              <p className="text-sm font-semibold text-foreground">
                No matches
              </p>
              <p className="text-xs mt-1">Try clearing the filters above.</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Listing</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead className="text-right">Update</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => {
                    const listing = listings.find(
                      (l) => l.id === lead.listing_id,
                    );
                    const initials = lead.name
                      .split(" ")
                      .map((p) => p[0])
                      .filter(Boolean)
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();
                    const pip = lead.platform
                      ? platformPip[lead.platform]
                      : null;
                    // Only one applicant can hold a listing — if someone else
                    // already does, "Approved" is off the table for this row.
                    const approvedHolder = findApprovedApplicant(
                      activeLeads,
                      lead.listing_id,
                      lead.id,
                    );
                    const days = Math.max(
                      0,
                      Math.floor(
                        (Date.now() - new Date(lead.created_at).getTime()) /
                          86400000,
                      ),
                    );
                    const relDate =
                      days === 0
                        ? "today"
                        : days === 1
                          ? "yesterday"
                          : `${days} days ago`;
                    return (
                      <TableRow
                        key={lead.id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => setSelectedLead(lead)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white grid place-items-center text-[11px] font-bold flex-shrink-0">
                              {initials}
                            </div>
                            <span className="font-semibold text-foreground text-[13px]">
                              {lead.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-[12.5px] text-muted-foreground max-w-[160px] truncate">
                          {listing?.title || "—"}
                        </TableCell>
                        <TableCell>
                          {pip ? (
                            <div className="flex items-center gap-1.5">
                              <span
                                className={cn(
                                  "inline-grid place-items-center w-5 h-5 rounded-[5px] text-[10px] font-black text-white flex-shrink-0",
                                  pip.bg,
                                )}
                              >
                                {pip.initial}
                              </span>
                              <span className="text-[12px] text-muted-foreground">
                                {getPlatformName(lead.platform!)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border",
                              leadStatusBadge[lead.status] ??
                                "bg-muted text-muted-foreground border-border",
                            )}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {lead.status.replace(/_/g, " ")}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {lead.email && <div>{lead.email}</div>}
                          {lead.phone && <div>{lead.phone}</div>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {relDate}
                        </TableCell>
                        <TableCell className="text-right">
                          <Select
                            value={lead.status}
                            onValueChange={(v) =>
                              updateLead.mutate({
                                id: lead.id,
                                status: v as any,
                              })
                            }
                          >
                            <SelectTrigger className="w-[130px] h-7 text-xs bg-muted border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">
                                Contacted
                              </SelectItem>
                              <SelectItem value="showing_scheduled">
                                Showing
                              </SelectItem>
                              <SelectItem value="application_sent">
                                Application
                              </SelectItem>
                              <SelectItem
                                value="approved"
                                disabled={!!approvedHolder}
                              >
                                Approved
                                {approvedHolder && (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    · {approvedHolder.name} holds it
                                  </span>
                                )}
                              </SelectItem>
                              <SelectItem value="denied">Denied</SelectItem>
                              <SelectItem value="lost">Lost</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* ── Applicant History tab ── */}
        <TabsContent value="history" className="mt-5 space-y-3">
          {archivedLeads.length === 0 ? (
            <div className="text-center py-14 border border-dashed border-border rounded-xl bg-card text-muted-foreground">
              <Archive className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium text-foreground">
                No applicant history yet.
              </p>
              <p className="text-xs mt-1 max-w-sm mx-auto">
                When you delete a listing, the applications on it are kept here
                against the property they applied for, instead of being deleted.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {archivedLeads.length} past applicant
                {archivedLeads.length !== 1 ? "s" : ""} — their listing has been
                deleted, so these records are read-only.
              </p>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Applied for</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Last status</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Applied</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedLeads.map((lead) => {
                      const initials = lead.name
                        .split(" ")
                        .map((p) => p[0])
                        .filter(Boolean)
                        .join("")
                        .slice(0, 2)
                        .toUpperCase();
                      const pip = lead.platform
                        ? platformPip[lead.platform]
                        : null;
                      // The listing is gone, so there's no title to read like
                      // the Applications tab does — pipeline_property_id is
                      // what survives the delete, and the property's address is
                      // what that title was seeded from anyway.
                      const propData = lead.pipeline_property_id
                        ? getPropertyData(lead.pipeline_property_id)
                        : null;
                      const propLabel = appliedForLabel(propData) ?? "—";
                      return (
                        <TableRow key={lead.id}>
                          <TableCell>
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-muted border border-border text-muted-foreground grid place-items-center text-[11px] font-bold flex-shrink-0">
                                {initials}
                              </div>
                              <span className="font-semibold text-foreground text-[13px]">
                                {lead.name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-[12.5px] text-muted-foreground max-w-[200px] truncate">
                            {propLabel}
                          </TableCell>
                          <TableCell>
                            {pip ? (
                              <div className="flex items-center gap-1.5">
                                <span
                                  className={cn(
                                    "inline-grid place-items-center w-5 h-5 rounded-[5px] text-[10px] font-black text-white flex-shrink-0",
                                    pip.bg,
                                  )}
                                >
                                  {pip.initial}
                                </span>
                                <span className="text-[12px] text-muted-foreground">
                                  {getPlatformName(lead.platform!)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                "inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border",
                                leadStatusBadge.archived,
                              )}
                            >
                              <Archive className="w-2.5 h-2.5" />
                              listing deleted
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {lead.email && <div>{lead.email}</div>}
                            {lead.phone && <div>{lead.phone}</div>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {fmtRelDays(lead.created_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreateListingDialog
        open={createOpen || !!editingListing}
        editingListing={editingListing}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditingListing(null);
          }
        }}
      />
      <PublishDialog
        open={!!publishListing}
        onOpenChange={(o) => !o && setPublishListing(null)}
        listing={publishListing}
      />
      <AddLeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        listings={listingsForAddLead}
      />
      <ListingPhotosDialog
        open={!!photosListing}
        onOpenChange={(o) => !o && setPhotosListing(null)}
        listing={photosListing}
      />
      <ListingDetailSheet
        listing={detailListing}
        open={!!detailListing}
        onOpenChange={(o) => !o && setDetailListing(null)}
        leads={leads}
        syndications={syndications}
        propData={
          detailListing
            ? getPropertyData(detailListing.pipeline_property_id)
            : null
        }
        onViewApplications={() => {
          if (detailListing) {
            setAppFilters((f) => ({ ...f, listingId: detailListing.id }));
            setActiveTab("applications");
          }
          setDetailListing(null);
        }}
        onEdit={() => {
          setEditingListing(detailListing);
          setDetailListing(null);
        }}
        onDelete={() => setDeletingListing(detailListing)}
        onUpdateStatus={(status) => {
          if (detailListing) {
            updateListing.mutate({ id: detailListing.id, status });
            setDetailListing((d) => (d ? { ...d, status } : null));
          }
        }}
        onRemoveFromRent={() => {
          if (!detailListing) return;
          const approvedLeads = leads.filter(
            (l) => l.listing_id === detailListing.id && l.status === "approved",
          );
          if (approvedLeads.length > 0) {
            // Declining the approved applicant frees the unit — updateLead's
            // side-effect flips the listing back to "active" automatically.
            approvedLeads.forEach((l) =>
              updateLead.mutate({ id: l.id, status: "denied" }),
            );
          } else {
            updateListing.mutate({ id: detailListing.id, status: "active" });
          }
          setDetailListing(null);
        }}
      />
      <ApplicationDetailSheet
        lead={selectedLead}
        listing={
          selectedLead
            ? (listings.find((l) => l.id === selectedLead.listing_id) ?? null)
            : null
        }
        propData={
          selectedLead
            ? getPropertyData(
                listings.find((l) => l.id === selectedLead.listing_id)
                  ?.pipeline_property_id ?? "",
              )
            : null
        }
        approvalBlockedBy={
          selectedLead
            ? (findApprovedApplicant(
                activeLeads,
                selectedLead.listing_id,
                selectedLead.id,
              )?.name ?? null)
            : null
        }
        open={!!selectedLead}
        onOpenChange={(o) => !o && setSelectedLead(null)}
        onUpdateStatus={(status) => {
          if (selectedLead) {
            updateLead.mutate({ id: selectedLead.id, status });
            setSelectedLead((l) => (l ? { ...l, status } : null));
          }
        }}
      />
      <DeleteConfirmDialog
        open={!!deletingListing}
        onOpenChange={(o) => !o && setDeletingListing(null)}
        title="Delete this listing?"
        description={
          deletingListing ? (
            <>
              <span className="font-semibold text-foreground">
                {deletingListing.title}
              </span>{" "}
              will be permanently deleted. This can't be undone — the property
              becomes available to list again.
              {deletingLeadCount > 0 && (
                <>
                  {" "}
                  Its {deletingLeadCount} application
                  {deletingLeadCount === 1 ? "" : "s"} are kept and moved to
                  Applicant History.
                </>
              )}
              {publishedPlatformCount > 0 && (
                <>
                  {" "}
                  It stays live on the {publishedPlatformCount} platform
                  {publishedPlatformCount === 1 ? "" : "s"} it was published to
                  until that listing is removed there.
                </>
              )}
            </>
          ) : null
        }
        confirmLabel="Delete listing"
        isPending={deleteListing.isPending}
        onConfirm={handleDeleteListing}
      />
    </div>
  );
}
